import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import {
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconX,
  IconLanguage,
  IconDotsVertical,
} from '@tabler/icons-react'
import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Checkbox'
import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { DoubleChevron } from '@/components/icons/DoubleChevron'
import { CHANNEL_TYPE } from '@/utils/constants'

import { SearchableDropdown } from '../dropdowns/SearchableDropdown'

import './TranscriptHeader.scss'

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
]

function findLanguageLabel(code) {
  const language = LANGUAGE_OPTIONS.find((lang) => lang.value === code)
  return language?.label || code
}

function buildLanguageDropdownOptions(translatedLanguages) {
  const translatedSet = new Set(translatedLanguages)
  const translatedOptions = LANGUAGE_OPTIONS.filter((lang) => translatedSet.has(lang.value))
  const untranslatedOptions = LANGUAGE_OPTIONS.filter((lang) => !translatedSet.has(lang.value))

  return [...translatedOptions, ...untranslatedOptions]
}

function getTranslateButtonLabel(translationState, selectedLanguage) {
  if (translationState === 'translating') {
    return 'Translating...'
  }
  if (translationState === 'translated' && selectedLanguage !== 'original') {
    return findLanguageLabel(selectedLanguage)
  }
  return 'Translate'
}

export const TranscriptHeader = ({
  headerTitle = 'Transcript',
  channelType,
  searchQuery,
  handleSearchTranscript,
  handleAllowAutoScrollChange,
  showTranscriptSelector,
  allowAutoScroll,
  transcriptSelectorOptions,
  handleTranscriptSelectionChange,
  transcriptType,
  enableAutoScroll,
  totalMatches = 0,
  currentSearchIndex = 0,
  handleNextSearchMatch,
  handlePreviousSearchMatch,
  handleClearSearch,
  handleToggleAll,
  allExpanded,
  threadLength = 0,
  keywords,
  onKeywordClick,
  selectedKeyword,
  showTranslateFeature = false,
  onLanguageChange,
  onShowOriginal,
  translationState = 'idle',
  selectedLanguage = 'original',
  translatedLanguages = [],
}) => {
  const [searchExpanded, setSearchExpanded] = useState(false)

  const isSmsOrChat = channelType === CHANNEL_TYPE.SMS || channelType === CHANNEL_TYPE.CHAT
  const isEmail = channelType === CHANNEL_TYPE.EMAIL
  const displayTitle = isSmsOrChat ? 'Message Transcript' : headerTitle

  const renderToggleAllButton = (testId = 'toggle-all') => {
    const disabled = !handleToggleAll || threadLength <= 1

    return (
      <>
        <div className="vertical-separator" />
        <Button
          square
          small
          onClick={handleToggleAll}
          data-testid={testId}
          disabled={disabled}
          tooltip={allExpanded ? 'Collapse all' : 'Expand all'}
        >
          <DoubleChevron flipped={allExpanded} />
        </Button>
      </>
    )
  }

  useEffect(() => {
    if (keywords && keywords.length > 0) {
      setSearchExpanded(true)
    }
  }, [])

  const handleCloseSearch = () => {
    setSearchExpanded(false)
    handleClearSearch()
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleNextSearchMatch()
      event.preventDefault()
    }
  }

  const handleLanguageSelect = (value) => {
    onLanguageChange?.(value)
  }

  const isShowingTranslation = translationState === 'translated' && selectedLanguage !== 'original'
  const isTranslating = translationState === 'translating'

  const renderHeaderContent = () => {
    if (!showTranslateFeature) {
      return <h3>{displayTitle}</h3>
    }

    const buttonLabel = getTranslateButtonLabel(translationState, selectedLanguage)

    return (
      <div className="translate-container">
        <SearchableDropdown
          options={buildLanguageDropdownOptions(translatedLanguages)}
          selected={selectedLanguage}
          handleSelect={handleLanguageSelect}
          additionalIcon={<IconLanguage className="language-icon" size={16} />}
          additionalLabel={<span className="translate-button-label">{buttonLabel}</span>}
          buttonProps={{ small: true }}
          hideLabel
          hideSearch
          fitContent
          dataTestId="translate-dropdown"
          isDisabled={isTranslating || isShowingTranslation}
        />
        {isShowingTranslation && onShowOriginal && (
          <Button link onClick={onShowOriginal}>
            Show Original
          </Button>
        )}
      </div>
    )
  }

  return (
    <header className={classNames('transcript-header', { 'search-expanded': searchExpanded })}>
      {!searchExpanded && renderHeaderContent()}

      {searchExpanded ? (
        <div className="search-container">
          <div className="search-row">
            <TextInput
              small
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchTranscript}
              onKeyDown={handleKeyDown}
              className="transcript-search-input"
              data-testid="transcript-search"
              autoFocus
            />

            <div className="search-controls">
              {searchQuery && (
                <>
                  <span className="search-count">
                    {totalMatches > 0 ? `${currentSearchIndex + 1} of ${totalMatches}` : '0 of 0'}
                  </span>
                  <Button
                    square
                    basic
                    small
                    onClick={handlePreviousSearchMatch}
                    data-testid="prev-search-match"
                  >
                    <IconChevronUp />
                  </Button>
                  <Button
                    square
                    basic
                    small
                    onClick={handleNextSearchMatch}
                    data-testid="next-search-match"
                  >
                    <IconChevronDown />
                  </Button>
                  <div className="vertical-separator" />
                </>
              )}
              <Button square basic small onClick={handleCloseSearch} data-testid="close-search">
                <IconX />
              </Button>
              {isEmail && renderToggleAllButton('toggle-all-search')}
            </div>
          </div>
          {keywords && keywords.length > 0 && (
            <div className="transcript-options-container">
              <span>Keywords searched:</span>
              <div className="flex-wrap small-gap">
                {keywords.map((keyword) => (
                  <Pill
                    key={keyword}
                    content={keyword}
                    clickable
                    onClick={() => onKeywordClick(keyword)}
                    informative={selectedKeyword === keyword}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="transcript-options-container">
          {!isSmsOrChat && enableAutoScroll && (
            <Toggle
              data-testid="auto-scroll-toggle"
              checked={allowAutoScroll}
              onChange={handleAllowAutoScrollChange}
              label="Auto-Scroll"
            />
          )}
          <div className="flex small-gap align-center">
            <Button
              square
              basic
              small
              onClick={() => setSearchExpanded(true)}
              data-testid="expand-search"
            >
              <IconSearch />
            </Button>
            {isEmail && renderToggleAllButton('toggle-all')}
          </div>

          {!isSmsOrChat && showTranscriptSelector && (
            <SearchableDropdown
              placement="bottom-end"
              icon={<IconDotsVertical />}
              hideSearch
              hideLabel
              options={transcriptSelectorOptions}
              selected={transcriptType}
              handleSelect={handleTranscriptSelectionChange}
              buttonProps={{ basic: true, square: true, small: true }}
              selectedOptionFirst={false}
            />
          )}
        </div>
      )}
    </header>
  )
}
